// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useState } from "react";

import { mdiDotsVertical } from "@mdi/js";
import { Button, Popover, Tooltip } from "antd";

import { ActionListWrapper } from "..";
import { ClearSelectedItemsAction } from "../actions/ClearSelectedItemsAction";
import { ContextMenuPopover } from "../context-menu/ContextMenuPopover";
import { UseRightClickContextMenuReturn } from "../hooks/useRightClickContextMenu";
import {
  ShapefileDeleteAction,
  ShapefileDeleteSelectedItemsAction,
  ShapefileExportAction,
  ShapefileMoveToBottomAction,
  ShapefileMoveToTopAction,
  ShapefilePromoteAction,
  ShapefileRemoveAction,
  ShapefileRenameAction,
  ShapefileSelectItemsAction,
  ShapefileViewDetailsAction
} from "./actions";

interface IShapefileActionsProps {
  deleteShapefile?: () => void;
  hasSelectItems?: boolean;
  isDisabled?: boolean;
  removeShapefile?: () => void;
  setIsRenaming?: () => void;
  viewShapefileDetails?: () => void;
  exportShapefile?: () => string;
  promoteShapefile?: (play: string, shapefileToOverwrite: string) => void;
  moveToTop?: () => void;
  moveToBottom?: () => void;
  deleteSelectedItemsActionText?: string;
  fileName: string;
  contextMenu?: UseRightClickContextMenuReturn;
}

/**
 * Essentially a wrapper for the shapefile actions, so that the actions can be used in different contexts.
 * @param params
 * @returns
 */
export function ShapefileActions(params: IShapefileActionsProps): JSX.Element {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const hasAnyActions = !!(
    params.viewShapefileDetails ||
    params.hasSelectItems ||
    params.setIsRenaming ||
    params.exportShapefile ||
    params.deleteShapefile ||
    params.removeShapefile ||
    params.promoteShapefile ||
    params.moveToTop ||
    params.moveToBottom
  );

  if (!hasAnyActions) {
    return null;
  }

  const actionList = (
    <ActionListWrapper>
      {params.viewShapefileDetails && (
        <ShapefileViewDetailsAction
          closePopover={() => {
            setIsPopoverOpen(false);
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
          viewShapefileDetails={params.viewShapefileDetails}
        />
      )}

      {params.setIsRenaming && (
        <ShapefileRenameAction
          isDisabled={params.isDisabled}
          setIsRenaming={() => {
            params.setIsRenaming();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
        />
      )}

      {params.exportShapefile && (
        <ShapefileExportAction
          isDisabled={params.isDisabled}
          closePopover={() => {
            setIsPopoverOpen(false);
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
          shapefileId={params.exportShapefile()}
          fileName={params.fileName}
        />
      )}

      {params.moveToTop && (
        <ShapefileMoveToTopAction
          isDisabled={params.isDisabled}
          moveToTop={params.moveToTop}
          onContextMenuClose={() => {
            setIsPopoverOpen(false);
            params.contextMenu?.onContextMenuClose?.();
          }}
        />
      )}

      {params.moveToBottom && (
        <ShapefileMoveToBottomAction
          isDisabled={params.isDisabled}
          moveToBottom={params.moveToBottom}
          onContextMenuClose={() => {
            setIsPopoverOpen(false);
            params.contextMenu?.onContextMenuClose?.();
          }}
        />
      )}

      {params.hasSelectItems && (
        <ShapefileSelectItemsAction
          setPopoverOpen={setIsPopoverOpen}
          onContextMenuClose={params.contextMenu?.onContextMenuClose}
        />
      )}

      {params.hasSelectItems && (
        <ClearSelectedItemsAction
          onContextMenuClose={params.contextMenu?.onContextMenuClose}
        />
      )}

      {params.deleteShapefile && (
        <ShapefileDeleteAction
          deleteShapefile={() => {
            params.deleteShapefile();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
          isDisabled={params.isDisabled}
        />
      )}

      {params.removeShapefile && (
        <ShapefileRemoveAction
          isDisabled={params.isDisabled}
          removeShapefile={() => {
            params.removeShapefile();
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
        />
      )}

      {params.hasSelectItems && (
        <ShapefileDeleteSelectedItemsAction
          onContextMenuClose={params.contextMenu?.onContextMenuClose}
          actionText={params.deleteSelectedItemsActionText}
        />
      )}

      {params.promoteShapefile && (
        <ShapefilePromoteAction
          isDisabled={params.isDisabled}
          closePopover={() => {
            setIsPopoverOpen(false);
            if (params.contextMenu?.onContextMenuClose)
              params.contextMenu.onContextMenuClose();
          }}
          promoteShapefile={(play, shapefileToOverwrite) =>
            params.promoteShapefile(play, shapefileToOverwrite)
          }
        />
      )}
    </ActionListWrapper>
  );

  const contextMenuPopover = (
    <ContextMenuPopover
      actionList={<div data-testid="shapefile-context-menu">{actionList}</div>}
      contextMenu={params.contextMenu}
    />
  );

  const actionButton = (
    <Tooltip title="Actions">
      <Popover
        arrowPointAtCenter={true}
        content={<div data-testid="shapefile-context-menu">{actionList}</div>}
        onOpenChange={(isOpen) => {
          setIsPopoverOpen(isOpen);
        }}
        open={isPopoverOpen}
        overlayClassName="popover-no-padding"
        placement="bottomLeft"
        trigger="click">
        <Button
          type="text"
          className="shapefile-actions"
          aria-label="Edit"
          data-testid="shapefile-actions-button">
          <Icon path={mdiDotsVertical} size={"21px"} />
        </Button>
      </Popover>
    </Tooltip>
  );

  return (
    <>
      {contextMenuPopover}
      {actionButton}
    </>
  );
}
